import { useMedplumProfile } from '@medplum/react';
import { Practitioner } from '@medplum/fhirtypes';
import { getReferenceString } from '@medplum/core';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useEffect } from 'react';

/**
 * Hook that listens for push notification clicks from the service worker
 * and tracks them in PostHog for analytics
 */
export function useNotificationAnalytics(): void {
  const posthog = usePostHog();
  const profile = useMedplumProfile() as Practitioner | undefined;

  const extractCommonProperties = useCallback(
    (eventData: Record<string, any>): Record<string, any> => {
      const baseProperties = {
        provider: profile ? getReferenceString(profile) : undefined,
        timestamp: eventData.timestamp,
        browser: navigator.userAgent,
      };

      return {
        ...baseProperties,
        title: eventData.title,
        tag: eventData.tag,
        taskId: eventData.taskId,
        communicationId: eventData.communicationId,
        category: eventData.category,
      };
    },
    [profile]
  );

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; data?: Record<string, any> } | undefined;

      if (!data || !data.type) {
        return;
      }

      const eventData = data.data || {};
      const commonProperties = extractCommonProperties(eventData);

      // Handle different notification event types
      switch (data.type) {
        case 'NOTIFICATION_SHOWN':
          posthog.capture('notification shown', commonProperties);
          break;

        case 'NOTIFICATION_CLICKED':
          posthog.capture('notification clicked', {
            ...commonProperties,
            action: eventData.action,
          });
          break;

        case 'NOTIFICATION_CLOSED':
          posthog.capture('notification closed', commonProperties);
          break;

        case 'NOTIFICATION_ERROR':
          posthog.capture('notification error', {
            ...commonProperties,
            error: eventData.error,
          });
          break;

        case 'CALL_ACTION': {
          const action = eventData.action as string | undefined;
          const callSid = (eventData.payload?.call?.sid ?? eventData.payload?.call?.from) as string | undefined;
          if (action === 'accept') {
            posthog.capture('call accepted', { ...commonProperties, callSid });
          } else if (action === 'reject') {
            posthog.capture('call rejected', { ...commonProperties, callSid });
          }
          break;
        }

        default:
          // Ignore other message types
          break;
      }
    };

    // Listen for messages from the service worker
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handler);
    }

    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handler);
      }
    };
  }, [posthog, profile, extractCommonProperties]);
}
